/**
 * AppReadyProvider
 *
 * Industry-standard solution for preventing flash of unauthorized content.
 * This provider ensures the app doesn't render protected routes until:
 * 1. Auth state is fully initialized
 * 2. User permissions are loaded (if authenticated)
 *
 * This eliminates:
 * - Flash of restricted content during loading
 * - Race conditions between auth and permissions
 * - Inconsistent UI states
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useAuth } from "./useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { PageLoader } from "@/components/ui/page-loader";
import { AppReadyContext, type AppReadyContextType } from "./app-ready-context";

// ============================================
// Loading Screen Component
// ============================================

interface LoadingScreenProps {
  message?: string;
}

export function AppLoadingScreen({ message = "Loading" }: LoadingScreenProps) {
  return (
    <div className="min-h-screen bg-light-gray">
      <PageLoader message={message} withBrand className="min-h-screen" />
    </div>
  );
}

// ============================================
// Gate decision
// ============================================

/**
 * Should the app be replaced by a loading screen?
 *
 * Extracted and exported so the rule is testable, because getting it wrong
 * unmounts the entire application mid-session. It previously keyed off
 * "permissions are currently loading", which is true during every refresh and
 * therefore tore the tree down whenever any page called refreshPermissions().
 */
export function shouldBlockAppRender(params: {
  authInitialized: boolean;
  isAuthenticated: boolean;
  /** Permissions resolved at least once FOR THE CURRENT USER. */
  permissionsHaveLoadedOnce: boolean;
}): boolean {
  if (!params.authInitialized) return true;
  if (params.isAuthenticated && !params.permissionsHaveLoadedOnce) return true;
  return false;
}

// ============================================
// Provider Component
// ============================================

interface AppReadyProviderProps {
  children: ReactNode;
  /** Custom loading component */
  loadingComponent?: ReactNode;
}

export function AppReadyProvider({
  children,
  loadingComponent,
  // minLoadingTime = 0,
}: AppReadyProviderProps) {
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const { hasLoadedOnce } = usePermissions();
  const [error, setError] = useState<string | null>(null);
  const [isStable, setIsStable] = useState(false);

  // Track when auth initialization is complete
  const authInitialized = !authLoading;

  // Gate on hasLoadedOnce, NOT on !isLoading.
  //
  // isLoading dips true on every refreshPermissions() call, which made this
  // provider swap the entire app for a loading screen mid-session — unmounting
  // whichever page triggered the refresh. On /auth/callback that was fatal: the
  // page calls refreshPermissions() and is unmounted before it reaches its
  // navigate(), so the redirect is silently skipped. The page then remounts
  // with fresh refs, re-runs bootstrap, refreshes permissions again, and the
  // sign-in spins forever.
  //
  // hasLoadedOnce still blocks the FIRST load (so no flash of unauthorized
  // content) and resets when the user changes, but a same-user refresh now
  // updates in place instead of tearing the tree down.
  const permissionsLoaded = hasLoadedOnce;

  // Determine if app is ready (based on actual data loading)
  const dataReady = useMemo(
    () =>
      !shouldBlockAppRender({
        authInitialized,
        isAuthenticated,
        permissionsHaveLoadedOnce: permissionsLoaded,
      }),
    [authInitialized, isAuthenticated, permissionsLoaded],
  );

  // Flip in the effect body, NOT inside requestAnimationFrame.
  //
  // Browsers do not run animation frames for a hidden or fully occluded tab,
  // so gating the very first render on rAF meant an admin link opened in a
  // background tab — or a tab the user switched away from while the splash was
  // up — never got past "Loading platform…" until it was focused again. The
  // callback simply never fired. Nothing here needs to wait for a paint: the
  // flash this guarded against is already prevented by `hasLoadedOnce`, which
  // keeps the gate closed until permissions have resolved for this user.
  useEffect(() => {
    setIsStable(dataReady);
  }, [dataReady]);

  // App is ready when data is loaded AND stable
  const isReady = dataReady && isStable;

  const refresh = useCallback(() => {
    setIsStable(false);
    setError(null);
  }, []);

  const value = useMemo<AppReadyContextType>(
    () => ({
      isReady,
      authInitialized,
      permissionsLoaded,
      error,
      refresh,
    }),
    [isReady, authInitialized, permissionsLoaded, error, refresh],
  );

  // Show loading screen while not ready
  if (!isReady) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    let message = "Initializing";
    if (!authInitialized) {
      message = "Verifying · Session";
    } else if (isAuthenticated && !permissionsLoaded) {
      message = "Loading · Permissions";
    }

    return <AppLoadingScreen message={message} />;
  }

  return (
    <AppReadyContext.Provider value={value}>
      {children}
    </AppReadyContext.Provider>
  );
}
